import './pciDataSharedWithPartners.less'
import {
  ACTION_FETCH_PCI_SHARED_WITH_PARTNERS,
  fetchPCISharedWithPartners
} from '../../../complianceDashboardSlice'
import ChartBarSimple, {
  ChartBarDataItem
} from '../../../../../../components/ChartBarSimple/chartBarSimple'
import CustomTooltip from '../../../../../../components/ChartTooltipV2'
import LoaderView from '../../../../../../components/Loader/loader'

import useWindowSize from '../../../../../../hooks/useWindowResize'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { RootState } from '../../../../../../rootReducer'
import { getWindowProps } from '../../../../../../utils'
import {
  UNIQUE_WIDGET_INDEX,
  getUniqueWidgetIndex,
  ChartColors
} from '../../../../mainDashboardv2/constants'
import { AttributeSetData, PCISharedWithPartnersData } from '../../../queries'
import { mockPCISharedWithPartnersData } from '../../mocks'
import WidgetTitle from '../../../../../../components/WidgetTitle'
import Card from '../../../../../accessControl/components/card'
import historyService from '../../../../../../services/historyService'
import { getUpdatedUrl } from '../../../../../../utils/urlUtil'
import {
  DATA_SOURCE_TYPE,
  FILTER_ENTITY_KEY,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  URL_DATA_SOURCES,
  URL_DOCUMENTS_ALL,
  URL_DOCUMENTS_CLASSIFIED,
  URL_OVERVIEW,
  URL_PRIVACY_PARTNERS
} from '../../../../../../constants'
import { hasData } from '../../helper'
import { EmptyState } from '../../emptyState/emptyState'
import EmptyStateIcon from '../../../../../../assets/inlineSvg/partners-widget-empty-state.svg'
import { ComplianceDashboardTypes } from '../../../constants'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import { Typography, Icon, Tooltip } from '@lightbeamai/design-system'
import React, { useEffect, useState, useMemo } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'
import { connect } from 'react-redux'

export const PCI_DATA_SHARED_WITH_PARTNERS_TABS = {
  BY_PARTNER: 'by_partner',
  BY_DATASOURCE: 'by_datasource'
} as const

export type PCIDataSharedWithPartnersTabType = typeof PCI_DATA_SHARED_WITH_PARTNERS_TABS[keyof typeof PCI_DATA_SHARED_WITH_PARTNERS_TABS]

interface OwnProps {
  useMockData?: boolean
  chartBarAlignment?: 'vertical' | 'horizontal'
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDashboardType?: string
  attributeSetId?: string
}

interface StateProps {
  widget?: PCISharedWithPartnersData | null
  attributeSetData?: AttributeSetData | null
  showLoader: boolean
}

interface DispatchProps {
  fetchPCISharedWithPartners: (params: {
    attributeSetId: string
    [UNIQUE_WIDGET_INDEX]?: string
  }) => void
}

type PCIDataSharedWithPartnersComponentProps = StateProps & DispatchProps & OwnProps

const PCIDataSharedWithPartners = (props: PCIDataSharedWithPartnersComponentProps) => {
  const {
    widget,
    showLoader,
    chartBarAlignment = 'horizontal',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    attributeSetData,
    fetchPCISharedWithPartners
  } = props

  const intl = useIntl()

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  const windowProps = getWindowProps(useWindowSize())
  const { isLargeScreen } = windowProps

  const titleTabs = [
    {
      key: PCI_DATA_SHARED_WITH_PARTNERS_TABS.BY_PARTNER,
      name: intl.formatMessage({
        id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.tab.partner'
      })
    },
    {
      key: PCI_DATA_SHARED_WITH_PARTNERS_TABS.BY_DATASOURCE,
      name: intl.formatMessage({
        id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.tab.datasources'
      })
    }
  ]

  const [activeTab, setActiveTab] = useState<PCIDataSharedWithPartnersTabType>(titleTabs[0].key)

  const handleTabChange = (tab) => setActiveTab(tab)
  const isPartnersTab = activeTab === PCI_DATA_SHARED_WITH_PARTNERS_TABS.BY_PARTNER

  useEffect(() => {
    if (useMockData) return
    if (!attributeSetData?.id) return
    fetchPCISharedWithPartners({
      attributeSetId: attributeSetData?.id || '',
      [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    })
  }, [
    useMockData,
    fetchPCISharedWithPartners,
    attributeSetData,
    widgetDashboardId,
    widgetDatasourceId
  ])

  const widgetData = useMemo(() => {
    if (isPartnersTab) {
      return (
        widget?.partners?.map((partner) => ({
          id: partner.id,
          name: partner.name,
          objectCount: partner.objectsCount || 0
        })) || []
      )
    } else {
      return (
        widget?.dataShareWithPartnersByDatasource?.map((datasource) => ({
          id: datasource.datasourceId,
          name: datasource.datasourceName,
          type: datasource.datasourceType,
          objectCount: datasource.objectsCount || 0
        })) || []
      )
    }
  }, [widget, activeTab])

  const chartsData = useMemo(() => {
    return widgetData.map((item) => {
      const chartItem: ChartBarDataItem = {
        name: item.name,
        value: item.objectCount,
        color: isPartnersTab ? ChartColors.Magenta : ChartColors.Blue,
        dataKey: item.id
      }

      if (!isPartnersTab) {
        chartItem.type = (item as any).type || ''
      }

      return chartItem
    })
  }, [widgetData, activeTab])

  const countsFormatter = (value: number) => intl.formatNumber(value, { notation: 'compact' })

  const handleClick = (val: any) => {
    const itemId = val?.dataKey || val?.payload?.dataKey
    if (itemId) {
      if (isPartnersTab) {
        historyService.push(
          getUpdatedUrl({
            pathName: `${URL_PRIVACY_PARTNERS}/${itemId}${URL_OVERVIEW}`,
            pageName: URL_PRIVACY_PARTNERS
          })
        )
      } else {
        historyService.push(
          getUpdatedUrl({
            pathName: `${URL_DATA_SOURCES}/${itemId}${URL_DOCUMENTS_ALL}`,
            pageName: URL_DOCUMENTS_CLASSIFIED,
            queryParams: {
              [DATA_SOURCE_TYPE]: val?.type || ''
            },
            pageQueryParams: {
              [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list
            }
          })
        )
      }
    }
  }

  const goToPartnersListPage = (hasEntities?: boolean) => {
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_PRIVACY_PARTNERS}`,
        pageName: `${URL_PRIVACY_PARTNERS}`,
        pageQueryParams: {
          [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
          ...(hasEntities
            ? { [FILTER_ENTITY_KEY]: intl.formatMessage({ id: 'filters.tab.entity.present' }) }
            : {})
        }
      })
    )
  }

  const filesText = intl.formatMessage({
    id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.chart.tooltip'
  })

  const cardTitle = (
    <WidgetTitle
      title={
        <div className="widget-title">
          <Typography color="primary" variant="h6" as="span">
            {intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.title'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: isPciDashboard
                ? 'compliance.dashboard.pci.widget.title.tooltip'
                : 'compliance.dashboard.hipaa.widget.title.tooltip'
            })}
            placement="right"
          >
            <Icon size={16} name="InfoCircle" className="pointer" />
          </Tooltip>
        </div>
      }
      tabs={titleTabs}
      onChange={handleTabChange}
      activeTab={activeTab}
      bordered
    />
  )

  const hasChartData = hasData(chartsData)

  return (
    <Card className="pci-data-shared-partners-widget" cardTitle={cardTitle}>
      <div className={`pci-data-shared-partners-chart-wrapper ${chartBarAlignment}`}>
        <div className="dashboard-labels-count-wrapper">
          <div className="label-items">
            <div className="label-item" onClick={() => goToPartnersListPage}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber value={widget?.partnersCount || 0} notation="compact" />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({
                  id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.summary.totalPartners'
                })}
              </Typography>
            </div>
            <div className="label-item" onClick={() => goToPartnersListPage}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber
                  value={widget?.dataShareWithPartnersByDatasource?.length || 0}
                  notation="compact"
                />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({
                  id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.summary.datasources'
                })}
              </Typography>
            </div>
            <div className="label-item" onClick={() => goToPartnersListPage(true)}>
              <Typography color="primary" type="strong" variant="h4">
                <FormattedNumber
                  value={widget?.entitiesCountSharedWithPartners || 0}
                  notation="compact"
                />
              </Typography>
              <Typography color="secondary" type="medium" variant="baseMinus2">
                {intl.formatMessage({
                  id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.summary.entities'
                })}
              </Typography>
            </div>
          </div>
        </div>
        {hasChartData ? (
          <div className="charts-wrapper pci-data-shared-partners-bar-charts xs-ml-24">
            <Typography as="span" variant="baseMinus2" className="xs-ml-32">
              {intl.formatMessage(
                {
                  id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.chartTitle'
                },
                {
                  distributionType: isPartnersTab
                    ? intl.formatMessage({
                        id:
                          'compliance.dashboard.pci.widget.dataSharedWithPartners.chartTitle.partner'
                      })
                    : intl.formatMessage({
                        id:
                          'compliance.dashboard.pci.widget.dataSharedWithPartners.chartTitle.datasources'
                      })
                }
              )}
            </Typography>
            <ChartBarSimple
              className="xs-mt-8 v2-chart"
              data={chartsData}
              cartesianGridHorrizontal
              tickFormatter={{
                fontSize: '0.72rem',
                fill: ChartColors.SecondaryText,
                width: 60,
                textAnchor: 'middle'
              }}
              multiColored
              chartMargin={{ left: 20, right: 20, top: 0, bottom: 0 }}
              barSize={60}
              chartResponsiveHeight={isLargeScreen ? 240 : 200}
              {...(isLargeScreen ? { xAxisHeight: 55 } : {})}
              yLabel={intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.dataSharedWithPartners.chart.ylabel'
              })}
              xAxisAngle={0}
              YTickFormatter={countsFormatter}
              yAxisLabelPadding={{ dx: -12, dy: -6 }}
              customToolTip={<CustomTooltip title={filesText} />}
              tickMargin={4}
              xAxisTickInterval={0}
              onBarItemClick={handleClick}
            />
          </div>
        ) : (
          <EmptyState
            title={intl.formatMessage({
              id: `compliance.dashboard.pci.widget.dataSharedWithPartners.errorText.${activeTab}`
            })}
            icon={<EmptyStateIcon />}
          />
        )}
      </div>
      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? mockPCISharedWithPartnersData.widget
    : state.complianceDashboard.pciSharedWithPartnersData?.[
        getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)
      ],
  attributeSetData: state.complianceDashboard.attributeSetData,
  showLoader: getShowLoader(ACTION_FETCH_PCI_SHARED_WITH_PARTNERS)
})

const mapDispatchToProps: DispatchProps = {
  fetchPCISharedWithPartners
}

export default connect(mapStateToProps, mapDispatchToProps)(PCIDataSharedWithPartners)
